// The Phaser side of weather (docs/20). Owned by the LightingLayer, which rebuilds it with the light
// map and passes on the mode, so weather obeys the same Full / Soft / Off setting and reduce motion.
//
// Where it draws (see LightingLayer's draw order):
//   light (bottom)   the ground shader's texture (rendered at art resolution) — clouds, wet earth,
//                    puddles and rings, snow / sand cover, rime and ice, heat shimmer, fog —
//                    under the light map, so night darkens it and torches light it
//   light (top)      lightning: an additive flash over the board (or at each exit, indoors)
//   low              splashes where rain lands, dust clouds in a sand storm (below the units)
//   high             rain and sleet streaks, snowflakes, blown grit (thin and translucent, over everything on the board)
// Nothing here is drawn on the overlay camera, and nothing moves under reduce motion.
import Phaser from 'phaser';
import type { TileData } from '../../core/sim/types';
import { isOpaque } from '../lighting/lightmap';
import { luma, mix, type DayLight, type RGB } from '../lighting/daylight';
import { cellHash } from '../tiling/hash';
import { GROUND_FRAG } from './groundShader';
import { FLASH_CAP, FLASH_GAP_MS, PUDDLE_DARK, weatherLabel, weatherLook, type Weather, type WeatherLook } from './weather';
import { nextStrike, storm, type Strike } from '../../core/audio/storm';
import { serverNow } from '../../core/time';
import { fieldFade } from '../board/field';
import { ViewRender } from '../board/viewRender';
import { ViewZone } from '../board/viewZone';

export interface WeatherLayers { light: Phaser.GameObjects.Layer; low: Phaser.GameObjects.Layer; high: Phaser.GameObjects.Layer }

export interface WeatherOptions {
  /** 1 full, 0.55 soft. */
  strength: number;
  /** Particles and lightning (Full only). */
  particles: boolean;
  /** Reduce motion: the shader stands still, nothing falls, nothing flashes. */
  still: boolean;
  /** The player allows lightning flashes. */
  flashes: boolean;
}

export interface WeatherInfo { kind: string; label: string; intensity: number; wind: number; fog: number; snow: number; wet: number; frost: number; heat: number; flashes: number; shader: boolean }

const hex = (c: RGB): number => (Math.round(Math.min(1, c[0]) * 255) << 16) | (Math.round(Math.min(1, c[1]) * 255) << 8) | Math.round(Math.min(1, c[2]) * 255);

let serial = 0;

function ensureTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists('fk-wx-streak')) {
    // a 1×8 rain streak, brightest at its leading (lower) end
    const c = tm.createCanvas('fk-wx-streak', 1, 8)!; const x = c.getContext();
    for (let i = 0; i < 8; i++) { x.fillStyle = `rgba(255,255,255,${(0.25 + 0.75 * (i / 7)).toFixed(2)})`; x.fillRect(0, i, 1, 1); }
    c.refresh();
  }
  if (!tm.exists('fk-wx-splash')) {
    // a tiny crown: two droplets either side of a dot
    const c = tm.createCanvas('fk-wx-splash', 5, 2)!; const x = c.getContext(); x.fillStyle = '#fff';
    x.fillRect(0, 0, 1, 1); x.fillRect(4, 0, 1, 1); x.fillRect(1, 1, 3, 1); c.refresh();
  }
  if (!tm.exists('fk-wx-grit')) {
    // a 4×1 streak of blown sand, brightest at its leading (east) end; rotated for a west wind
    const c = tm.createCanvas('fk-wx-grit', 4, 1)!; const x = c.getContext();
    for (let i = 0; i < 4; i++) { x.fillStyle = `rgba(255,255,255,${(0.2 + 0.8 * (i / 3)).toFixed(2)})`; x.fillRect(i, 0, 1, 1); }
    c.refresh();
  }
  if (!tm.exists('fk-wx-flake')) {
    const c = tm.createCanvas('fk-wx-flake', 3, 3)!; const x = c.getContext();
    x.fillStyle = 'rgba(255,255,255,0.55)'; x.fillRect(1, 0, 1, 3); x.fillRect(0, 1, 3, 1); x.fillStyle = '#fff'; x.fillRect(1, 1, 1, 1); c.refresh();
  }
}

export class WeatherLayer {
  private objects: Phaser.GameObjects.GameObject[] = [];
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private zones: ViewZone[] = [];
  private maskKey?: string;
  private shader?: Phaser.GameObjects.Shader;
  /** The ground pass, rendered only where the camera looks. */
  private view?: ViewRender;
  private groundKey?: string;
  private lastGround = -1;
  private flash: Phaser.GameObjects.Image[] = [];
  private look?: WeatherLook;
  private weather?: Weather;
  private opts: WeatherOptions = { strength: 1, particles: true, still: false, flashes: true };
  private time = 0;
  private frozenAt = 0;
  private nextFlash = 0;
  private sharedNext?: Strike;
  private manual = false;
  private flashStart = -1;
  private flashCount = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly layers: WeatherLayers, private readonly cell: number) {
    ensureTextures(scene);
  }

  build(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, day: DayLight, weather: Weather, opts: WeatherOptions): void {
    this.clear();
    this.weather = weather; this.opts = opts;
    const look = weatherLook(day, weather);
    this.look = look;
    const { scene, cell } = this;
    const W = board.w * cell, H = board.h * cell;
    const k = opts.strength;
    this.frozenAt = (cellHash(`${board.w}x${board.h}`, board.w, board.h, 'wx-time') % 600) / 10;

    if (scene.game.renderer.type === Phaser.WEBGL && (look.cloud > 0 || look.fog > 0 || look.wet > 0 || look.snow > 0 || look.frost > 0 || look.heat > 0)) {
      // one texel per cell: R open land, G water (0) or a puddle roll (64..255), B an exit
      const key = `fk-wx-mask-${++serial}`;
      const mask = scene.textures.createCanvas(key, board.w, board.h)!;
      const ctx = mask.getContext();
      const img = ctx.createImageData(board.w, board.h);
      for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) {
        const t = board.tiles[y * board.w + x];
        const i = (y * board.w + x) * 4;
        const water = t?.terrain === 'Water' || t?.terrain === 'Deep';
        img.data[i] = isOpaque(t) || water ? 0 : 255;
        img.data[i + 1] = water ? 0 : 64 + (cellHash('puddle', x, y) % 192);
        img.data[i + 2] = t?.exit ? 255 : 0;
        img.data[i + 3] = 255;
      }
      ctx.putImageData(img, 0, 0); mask.refresh(); mask.setFilter(Phaser.Textures.FilterMode.LINEAR);
      this.maskKey = key;
      // the shader runs once per art pixel into its own texture (not once per screen pixel at the
      // board's zoom — 9–16× cheaper, and crisp like the tiles), refreshed at 20 Hz in update()
      const s = new Phaser.GameObjects.Shader(scene, {
        name: 'fkWeatherGround',
        fragmentSource: GROUND_FRAG,
        setupUniforms: (set: (name: string, value: unknown) => void) => {
          set('uMask', 0);
          set('uSize', [W, H]);
          set('uCells', [board.w, board.h]);
          set('uTime', this.opts.still ? this.frozenAt : this.time / 1000);
          set('uWind', look.wind);
          set('uCloud', look.cloud * k);
          set('uCover', look.cover);
          set('uWet', look.wet * k);
          set('uDarkMax', look.darkMax);
          set('uFog', look.fog * k);
          set('uFogColour', [...look.fogColour]);
          set('uSnow', look.snow * k);
          set('uSky', [...look.sky]);
          set('uRipples', look.rain > 0 && !this.opts.still ? 1 : 0);
          set('uIndoor', look.indoor ? 1 : 0);
          set('uPuddleDark', PUDDLE_DARK * k);
          set('uFogSpeed', look.fogSpeed);
          set('uFogStretch', look.fogStretch);
          set('uSnowColour', [...look.snowColour]);
          set('uSnowCaps', look.snowCaps);
          set('uSnowDrifts', look.snowDrifts);
          set('uFrost', look.frost * k);
          set('uHeat', look.heat * k);
        },
      }, 0, 0, W, H, [key]);
      const tex = `fk-wx-ground-${serial}`;
      s.setRenderToTexture(tex);
      const ground = scene.add.image(0, 0, tex).setOrigin(0, 0).setDisplaySize(W, H);
      this.layers.light.addAt(ground, 0);
      this.shader = s; this.groundKey = tex; this.objects.push(ground, s);
      this.view = new ViewRender(s, scene.cameras.main, W, H);
      this.lastGround = -1;
    }

    if (look.flashEvery > 0 && opts.particles && opts.flashes && !opts.still) {
      const tint = hex(mix(look.sky, [0.85, 0.9, 1.0], 0.6));
      if (look.indoor) {
        // the storm is outside: lightning shows as a flare in each doorway
        for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) {
          if (!board.tiles[y * board.w + x]?.exit) continue;
          const f = scene.add.image((x + 0.5) * cell, (y + 0.5) * cell, 'fk-lt-soft').setDisplaySize(cell * 7, cell * 7).setBlendMode(Phaser.BlendModes.ADD).setTint(tint).setAlpha(0);
          this.flash.push(f);
        }
      } else {
        this.flash.push(scene.add.image(0, 0, 'fk-lt-dot').setOrigin(0, 0).setDisplaySize(W, H).setBlendMode(Phaser.BlendModes.ADD).setTint(tint).setAlpha(0));
      }
      for (const f of this.flash) { this.layers.light.add(f); this.objects.push(f); }
      this.nextFlash = 0;
    }

    if (opts.particles && !opts.still) this.buildParticles(board, day, look);
  }

  private buildParticles(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, day: DayLight, look: WeatherLook): void {
    const { scene, cell } = this;
    const W = board.w * cell, H = board.h * cell;
    const lum = Math.min(1, luma(day.ambient));
    // docs/38 BS7: on a free-form board the field's own edge
    const fade = fieldFade(board, cell);
    const inside = (p: Phaser.GameObjects.Particles.Particle, r: number): number => fade(p.x, p.y, r);
    const emit = (layer: Phaser.GameObjects.Layer, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Phaser.GameObjects.Particles.ParticleEmitter => {
      // docs/147 PG4: outdoors the weather falls only where it can reach the view (indoors it is the doorways)
      const near = !look.indoor ? ViewZone.wrap(scene.cameras.main, { x: 0, y: 0, w: W, h: H }, cfg) : undefined;
      const e = scene.add.particles(0, 0, tex, near ? near.cfg : cfg);
      if (near) { near.zone.attach(e); this.zones.push(near.zone); }
      layer.add(e); this.emitters.push(e);
      return e;
    };
    type Rect = { x: number; y: number; w: number; h: number };
    const zoneOf = (rects: Rect[]): Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig => ({
      type: 'random',
      source: { getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => { const r = rects[Math.floor(Math.random() * rects.length)]!; p.x = r.x + Math.random() * r.w; p.y = r.y + Math.random() * r.h; } },
    });
    // indoors the weather only falls in the doorways
    const exits: Rect[] = [];
    if (look.indoor) for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) if (board.tiles[y * board.w + x]?.exit) exits.push({ x: x * cell, y: y * cell, w: cell, h: cell });
    const area: Rect[] = look.indoor ? exits : [{ x: 0, y: 0, w: W, h: H }];
    if (area.length === 0) return;
    const cells = look.indoor ? exits.length * 4 : board.w * board.h;
    // indoors a drop is only seen while it is inside a doorway
    const within = (p: Phaser.GameObjects.Particles.Particle): number => !look.indoor ? 1 : exits.some((r) => p.x >= r.x && p.x < r.x + r.w && p.y >= r.y - 2 && p.y < r.y + r.h) ? 1 : 0;

    if (look.rain > 0) {
      // sleet falls a little faster and heavier, in shorter streaks
      const sleet = look.streak < 1;
      const fall = (300 + 60 * look.rain) * (sleet ? 1.15 : 1), drift = look.wind * 150;
      const angle = (-Math.atan2(drift, fall) * 180) / Math.PI;
      const tint = hex(mix([0.72, 0.8, 0.92], day.sky, 0.3));
      const splash = emit(this.layers.low, 'fk-wx-splash', {
        emitting: false, lifespan: { min: 160, max: 260 }, scaleX: { start: 0.6, end: 1.4 }, scaleY: { start: 1, end: 0.6 },
        alpha: { start: 0.55 * (0.55 + 0.45 * lum), end: 0 }, tint,
      });
      const rate = look.rain * cells * 2.4;   // streaks per second
      emit(this.layers.high, 'fk-wx-streak', {
        emitZone: zoneOf(area), frequency: 1000 / Math.max(1, rate), quantity: 1, lifespan: { min: 180, max: 420 },
        speedY: { min: fall * 0.9, max: fall * 1.1 }, speedX: { min: drift * 0.9, max: drift * 1.1 }, rotate: angle,
        scaleY: { min: 0.8 * look.streak, max: 1.6 * look.streak }, scaleX: sleet ? { min: 1, max: 2 } : 1, tint,
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (0.3 + 0.2 * look.rain + (sleet ? 0.12 : 0)) * (0.55 + 0.45 * lum) * Math.min(1, t * 6, (1 - t) * 6) * inside(p, cell * 0.5) * within(p) },
        deathCallback: (p: Phaser.GameObjects.Particles.Particle) => { if (Math.random() < 0.45 && inside(p, 2) > 0 && within(p) > 0) splash.emitParticleAt(Math.round(p.x), Math.round(p.y), 1); },
        advance: 500,
      });
    }

    if (look.snowfall > 0) {
      // a gale (blizzard) drives the flakes nearly sideways and faster
      const gale = look.gale;
      const drift = look.wind * (26 + 150 * gale), fall = 1 + 1.6 * gale;
      const tint = hex(mix([1, 1, 1], day.ambient, 0.35));
      const sway = (p: Phaser.GameObjects.Particles.Particle, t: number, v: number): number => v + Math.sin(t * 9 + p.life * 0.37) * 0.22;
      // far flakes: single pixels, slow
      emit(this.layers.high, 'fk-lt-dot', {
        emitZone: zoneOf(area), frequency: 1000 / Math.max(1, look.snowfall * cells * 0.6), lifespan: { min: 3500, max: 6500 },
        speedY: { min: 12 * fall, max: 22 * fall }, speedX: { min: drift * 0.7, max: drift * 1.1 }, scale: { min: 1, max: 1.5 }, tint,
        x: { onEmit: () => 0, onUpdate: (p, _k, t, v) => sway(p, t, v) },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.75 * Math.min(1, t * 5, (1 - t) * 4) * inside(p, cell * 0.6) },
        advance: 6000,
      });
      // near flakes: little crosses, faster and fewer — the parallax that makes it feel deep
      emit(this.layers.high, 'fk-wx-flake', {
        emitZone: zoneOf(area), frequency: 1000 / Math.max(1, look.snowfall * cells * 0.22), lifespan: { min: 2200, max: 3800 },
        speedY: { min: 28 * fall, max: 44 * fall }, speedX: { min: drift * 1.1, max: drift * 1.6 }, rotate: { min: 0, max: 90 }, tint,
        x: { onEmit: () => 0, onUpdate: (p, _k, t, v) => sway(p, t, v) },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.85 * Math.min(1, t * 5, (1 - t) * 4) * inside(p, cell * 0.6) },
        advance: 4000,
      });
    }

    if (look.grit > 0) {
      // a sand storm: fast low streaks of grit driven along the wind (over everything, thin), and big
      // soft dust clouds rolling through below the units (they never hide a body)
      const dir = look.wind < 0 ? -1 : 1, gust = Math.max(0.5, Math.abs(look.wind));
      const tint = hex(look.gritColour);
      emit(this.layers.high, 'fk-wx-grit', {
        emitZone: zoneOf(area), frequency: 1000 / Math.max(1, look.grit * cells * 2.2), lifespan: { min: 260, max: 620 },
        speedX: { min: dir * 200 * gust, max: dir * 320 * gust }, speedY: { min: -6, max: 14 }, scaleX: { min: 1, max: 2.5 }, rotate: dir < 0 ? 180 : 0, tint,
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (0.32 + 0.25 * look.grit) * (0.6 + 0.4 * lum) * Math.min(1, t * 5, (1 - t) * 5) * inside(p, cell * 0.5) * within(p) },
        advance: 600,
      });
      if (!look.indoor) {
        const puffTint = hex(mix(look.gritColour, look.fogColour, 0.5));
        emit(this.layers.low, 'fk-lt-soft', {
          emitZone: zoneOf(area), frequency: 1000 / Math.max(0.5, look.grit * cells * 0.05), lifespan: { min: 2200, max: 3600 },
          speedX: { min: dir * 50 * gust, max: dir * 90 * gust }, speedY: { min: -4, max: 4 }, scale: { min: 1.6, max: 3.2 }, tint: puffTint,
          alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.13 * look.grit * Math.sin(t * Math.PI) * inside(p, cell * 2) },
          advance: 3000,
        });
      }
    }
  }

  update(time: number): void {
    this.time = time;
    for (const z of this.zones) z.update();
    // (still: the first full render stands, as before; otherwise 20 Hz, and at once when the camera looks past what was rendered)
    if (this.view && !this.opts.still && (this.lastGround < 0 || time - this.lastGround >= 50 || this.view.needed)) { this.lastGround = time; this.view.render(); }
    if (this.flash.length === 0 || !this.look) return;
    // docs/34 SX11: strikes come from the shared storm schedule on the server clock, so every player
    // sees this flash (and hears its thunder, core/audio/ambience.ts) together; without one (the
    // gallery, a review page) the old local rhythm stands in
    const shared = storm();
    if (shared) {
      const now = serverNow();
      if (!this.sharedNext || now > this.sharedNext.at + 1000) this.sharedNext = nextStrike(shared.key, shared.every, now - 450);
      if (this.sharedNext && this.flashStart < 0 && now >= this.sharedNext.at && now < this.sharedNext.at + 450) {
        this.flashStart = time - (now - this.sharedNext.at); this.flashCount++;
        this.sharedNext = nextStrike(shared.key, shared.every, this.sharedNext.at);
      }
    } else {
      if (this.nextFlash === 0) this.nextFlash = time + this.look.flashEvery * (0.3 + Math.random() * 0.5);
      if (this.flashStart < 0 && time >= this.nextFlash) {
        this.flashStart = time; this.flashCount++;
        this.nextFlash = time + Math.max(FLASH_GAP_MS, this.look.flashEvery * (0.5 + Math.random()));
      }
    }
    if (this.manual) { this.manual = false; if (this.flashStart < 0) { this.flashStart = time; this.flashCount++; } }
    let a = 0;
    if (this.flashStart >= 0) {
      const t = time - this.flashStart;
      // a strike: bright, a dip, a second smaller pulse, then a long fade — about 0.45 s in all
      if (t < 70) a = 1 - t / 160;
      else if (t < 120) a = 0.35;
      else if (t < 450) a = 0.75 * (1 - (t - 120) / 330);
      else { a = 0; this.flashStart = -1; }
    }
    const cap = FLASH_CAP * this.opts.strength * (this.look.indoor ? 0.8 : 1);
    for (const f of this.flash) f.setAlpha(Math.max(0, a) * cap);
  }

  /** Strike now (review hook); does nothing when flashes are off. */
  strike(): void { if (this.flash.length) { this.nextFlash = this.time; this.manual = true; } }

  info(): WeatherInfo | undefined {
    const w = this.weather, l = this.look;
    if (!w || !l) return undefined;
    return { kind: w.kind, label: weatherLabel(w), intensity: w.intensity, wind: w.wind, fog: l.fog, snow: l.snow, wet: l.wet, frost: l.frost, heat: l.heat, flashes: this.flashCount, shader: !!this.shader };
  }

  clear(): void {
    for (const o of this.objects) o.destroy();
    for (const e of this.emitters) e.destroy();
    this.objects = []; this.emitters = []; this.zones = []; this.flash = []; this.shader = undefined; this.view = undefined;
    if (this.maskKey) this.scene.textures.remove(this.maskKey);
    if (this.groundKey && this.scene.textures.exists(this.groundKey)) this.scene.textures.remove(this.groundKey);
    this.maskKey = undefined; this.groundKey = undefined; this.look = undefined; this.weather = undefined;
    this.flashStart = -1; this.flashCount = 0;
  }
}
